// frontend/src/pages/LandingPage/ThankYouPage.jsx
// Página de Agradecimento - Após solicitação de demonstração
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    CheckCircle2, 
    Download,
    MessageSquare,
    Star
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';

const ThankYouPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [leadNome, setLeadNome] = useState('');

    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        
        // Recuperar dados do state de navegação
        if (location.state?.nome) {
            setLeadNome(location.state.nome);
        }

        // Google Ads Conversion Tracking (se configurado)
        if (window.gtag) {
            window.gtag('event', 'conversion', {
                'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',
                'value': 1.0,
                'currency': 'BRL'
            });
        }

        // Facebook Pixel (se configurado)
        if (window.fbq) {
            window.fbq('track', 'Lead');
        }
    }, [location.state]);

    // Depoimento fictício (substitua por dados reais)
    const depoimento = {
        nome: 'Maria Silva',
        cargo: 'Diretora Administrativa',
        lar: 'Lar Residencial Bem-Estar',
        texto: 'O Higeia transformou completamente nossa gestão. Reduzimos erros em 90% e ganhamos horas preciosas de trabalho administrativo.',
        avaliacao: 5
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main className="py-12 sm:py-20">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    
                    {/* Mensagem Principal de Agradecimento */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mb-6">
                            <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-4">
                            Obrigado! Sua Demonstração Foi{' '}
                            <span className="bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                                Solicitada com Sucesso
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {leadNome ? (
                                <>Nossa equipe entrará em contato com <strong>{leadNome}</strong> em até 24 horas para agendar sua sessão.</>
                            ) : (
                                <>Nossa equipe entrará em contato em até 24 horas para agendar sua sessão.</>
                            )}
                            <br />
                            <span className="text-lg">Fique atento ao seu e-mail e WhatsApp.</span>
                        </p>
                    </motion.div>

                    {/* Prova Social - Depoimento */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-gray-100 mb-12"
                    >
                        <div className="flex flex-col md:flex-row gap-6 items-start">
                            <div className="flex-shrink-0">
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                                    <MessageSquare className="h-8 w-8 text-emerald-600" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-3">
                                    {[...Array(depoimento.avaliacao)].map((_, i) => (
                                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                                    ))}
                                </div>
                                <blockquote className="text-lg text-gray-700 mb-4 italic">
                                    "{depoimento.texto}"
                                </blockquote>
                                <div className="border-t border-gray-200 pt-4">
                                    <p className="font-bold text-gray-900">{depoimento.nome}</p>
                                    <p className="text-sm text-gray-600">{depoimento.cargo}</p>
                                    <p className="text-sm text-emerald-600 font-semibold">{depoimento.lar}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Recurso de Baixo Atrito - Download de Checklist */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 sm:p-10 shadow-lg border border-gray-200"
                    >
                        <div className="flex flex-col md:flex-row gap-6 items-center">
                            <div className="flex-shrink-0">
                                <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center">
                                    <Download className="h-8 w-8 text-white" />
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                    Baixe Nosso Checklist de Gestão de ILPIs
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    Um guia prático com tudo que você precisa saber para gerenciar seu Lar de Idosos de forma eficiente. 
                                    Use hoje mesmo!
                                </p>
                                <PrimaryButton
                                    onClick={() => {
                                        // Aqui você pode implementar o download real
                                        // Por enquanto, apenas um exemplo
                                        console.log('Download do checklist');
                                        // window.open('/downloads/checklist-gestao-ilpi.pdf', '_blank');
                                        alert('Download será implementado. Em breve você receberá o checklist por e-mail!');
                                    }}
                                    variant="primary"
                                    size="md"
                                    icon={<Download className="h-4 w-4" />}
                                >
                                    Baixar Checklist Gratuitamente
                                </PrimaryButton>
                            </div>
                        </div>
                    </motion.div>

                    {/* Informações Adicionais */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8, duration: 0.6 }}
                        className="mt-12 text-center"
                    >
                        <p className="text-gray-600 mb-4">
                            Tem dúvidas ou prefere falar agora?
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <PrimaryButton
                                onClick={() => navigate('/contato')}
                                variant="secondary"
                                size="md"
                            >
                                Fale Conosco
                            </PrimaryButton>
                            <PrimaryButton
                                onClick={() => navigate('/recursos')}
                                variant="secondary"
                                size="md"
                            >
                                Conheça os Recursos
                            </PrimaryButton>
                        </div>
                    </motion.div>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default ThankYouPage;

